import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';

import Icon, { IconName } from '@/components/Icon/Icon';
import RichText from '@/components/RichText/RichText';
import type { AccordionContent } from '@/components/TopLevelComponents/GlobalFAQ/GlobalFAQ';
import useGlobalStyles from '@/styles/global.styles';
import { trackEvents } from '@/utils/analytics';

import useStyles from './FAQGroup.styles';

interface FAQGroupProps {
  faqs: Array<AccordionContent & { accordionIndex: number }>;
  faqState: boolean[];
  setFaqState: (state: boolean[]) => void;
  isOnDark: boolean;
}

export default function FAQGroup({
  faqs,
  faqState,
  setFaqState,
  isOnDark,
}: FAQGroupProps) {
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const { classes } = useStyles({ isOnDark });

  const handleChange =
    (index: number, question: string) =>
    // biome-ignore lint/correctness/noUnusedFunctionParameters: Automated migration to Biome
    (event: React.ChangeEvent<object>, isExpanded: boolean) => {
      const newFaqState = [...faqState];
      newFaqState[index] = isExpanded;
      trackEvents([
        {
          event: 'FAQ',
          faq_text: question,
          faq_placement: index,
        },
      ]);
      setFaqState(newFaqState);
    };
  return (
    <>
      {faqs.map((faq) => {
        const {
          fields: { summary, details },
          accordionIndex,
        } = faq;

        return (
          <Accordion
            key={accordionIndex}
            classes={{ root: classes.accordionRoot }}
            expanded={faqState[accordionIndex]}
            onChange={handleChange(accordionIndex, summary)}
            square
          >
            <AccordionSummary
              aria-controls={`panel${accordionIndex}a-content`}
              id={`panel${accordionIndex}a-header`}
              classes={{
                root: classes.accordionSummaryRoot,
                content: classes.accordionSummaryContent,
              }}
            >
              <div
                className={classes.expandIcon}
                style={{
                  paddingLeft: faqState[accordionIndex] ? '0' : '2px',
                }}
              >
                <Icon
                  name={
                    faqState[accordionIndex]
                      ? IconName.CHEVRON_DOWN
                      : IconName.CHEVRON_RIGHT
                  }
                  width={24}
                />
              </div>
              <h5 className={globalClasses.h5}>{summary}</h5>
            </AccordionSummary>
            <AccordionDetails classes={{ root: classes.accordionDetailsRoot }}>
              <RichText
                content={details}
                className={globalClasses.body}
                isOnDark={isOnDark}
              />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </>
  );
}
